import { useEffect, useId, useState } from 'react'
import Header from '../components/layout/Header'
import PortfolioBillboard from '../components/portfolio/PortfolioBillboard'
import { SUB_ACCOUNTS_SEO } from '../config/seo'
import { useWalletAuth } from '../context/WalletAuthProvider'
import { usePageCss } from '../hooks/usePageCss'
import { usePageMeta } from '../hooks/usePageMeta'
import { signalCellOpacity } from '../components/trade/positionsEmpty'
import './sub-accounts.css'

const CONNECT_CLASS =
  'group inline-flex w-fit shrink-0 cursor-pointer items-center justify-center truncate rounded-pill text-sm/sm font-medium transition-all active:scale-[0.97] disabled:cursor-auto disabled:opacity-50 disabled:active:scale-100 bg-ink text-bg shadow-border-button-primary hover:bg-ink/90 active:bg-ink/80 disabled:hover:bg-ink disabled:active:bg-ink light:shadow-(--btn-primary-shadow) h-8 gap-2 p-2 max-mobile:min-h-(--control-h-lg) rounded-card px-4'

const EMPTY_PANEL_CLASS =
  "flex flex-col items-center gap-5 rounded-panel border-[0.5px] border-line bg-overlay px-6 py-20 text-center shadow-[inset_0_var(--hl-y)_0_0_var(--hl-top),0px_0px_0px_0.5px_var(--ring-crisp),0px_1px_0px_0px_var(--edge-under),var(--shadow-sm)] max-mobile:py-14 light:border-transparent light:bg-overlay light:shadow-[0_0_0_0.5px_var(--ring-crisp),0_1px_0_0_var(--edge-under),var(--shadow-sm)]"

function openConnect() {
  window.dispatchEvent(new Event('lighter:open-connect'))
}

function DisconnectedPanel() {
  const patternId = useId().replace(/:/g, '')
  const [cells, setCells] = useState([])

  useEffect(() => {
    const started = performance.now()
    const period = 2600
    let raf = 0
    const tick = (now) => {
      const cycle = ((now - started) % period) / period
      const ping = cycle < 0.5 ? cycle * 2 : 2 - cycle * 2
      const t = ping * ping * (3 - 2 * ping)
      const next = []
      for (let col = 0; col < 11; col += 1) {
        for (let row = 0; row < 11; row += 1) {
          const op = signalCellOpacity(col, row, t)
          if (!op) continue
          next.push({
            key: `${col}-${row}`,
            x: 0.65 + col * 4,
            y: 0.65 + row * 4,
            op,
          })
        }
      }
      setCells(next)
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <div role="status" className={EMPTY_PANEL_CLASS} style={{ transform: 'none' }}>
      <span className="inline-flex">
        <svg
          viewBox="0 0 44 44"
          className="size-20 max-mobile:size-14 overflow-visible"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id={patternId}
              width="4"
              height="4"
              patternUnits="userSpaceOnUse"
            >
              <rect
                x="0.65"
                y="0.65"
                width="2.7"
                height="2.7"
                fill="currentColor"
                fillOpacity="0.09"
              />
            </pattern>
          </defs>
          <rect width="44" height="44" fill={`url(#${patternId})`} className="text-ink" />
          <g data-positions-signal-bars>
            {cells.map((cell) => (
              <rect
                key={cell.key}
                x={cell.x}
                y={cell.y}
                width="2.7"
                height="2.7"
                fill="currentColor"
                fillOpacity={cell.op}
              />
            ))}
          </g>
        </svg>
      </span>
      <div className="flex flex-col items-center gap-2">
        <span className="font-mono label-meta text-lg/lg text-ink">Not connected</span>
        <p className="max-w-[420px] text-md/md text-pretty text-dim">
          Connect a wallet to see your sub-accounts.
        </p>
      </div>
      <button type="button" className={CONNECT_CLASS} onClick={openConnect}>
        <span>Connect Wallet</span>
      </button>
    </div>
  )
}

export default function SubAccounts() {
  usePageMeta(SUB_ACCOUNTS_SEO)
  usePageCss('sub-accounts-page-css', '/css/lighter_sub-accounts.css')
  const { isConnected } = useWalletAuth()

  return (
    <div className="sub-accounts-page flex h-dvh w-full flex-col overflow-hidden bg-bg text-ink">
      <Header />
      <PortfolioBillboard />
      <div
        data-testid="sub-page-scroll-region"
        className="min-h-0 flex-1 overflow-y-auto"
        style={{ '--fade-t': '0px', '--fade-b': '0px' }}
      >
        <div className="mx-auto w-full max-w-[1240px] px-8 py-10 max-mobile:px-4 max-mobile:py-6">
          <section className="section-in max-mobile:mb-6 mb-8" style={{ '--sec-d': '0s' }}>
            <div className="flex items-end justify-between gap-6 max-mobile:flex-col max-mobile:items-start max-mobile:gap-4">
              <div className="min-w-0">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                  <h1 className="m-0 text-hero leading-[1.02] font-medium tracking-display [text-box-edge:cap_alphabetic] [text-box-trim:trim-start]">
                    Sub-Accounts
                  </h1>
                </div>
              </div>
              <div className="shrink-0 max-mobile:w-full">
                <div className="flex shrink-0 items-center gap-2 max-mobile:w-full max-mobile:flex-wrap"></div>
              </div>
            </div>
            <div className="mt-4 max-w-[60%] text-xl/2xl text-pretty text-dim max-mobile:max-w-none">
              Organize your capital across separate trading accounts with isolated risk.
            </div>
          </section>
          {isConnected ? null : <DisconnectedPanel />}
        </div>
      </div>
    </div>
  )
}
